import React, { useEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

import {
  FaArrowUp,
  FaBurger,
  FaFire,
  FaIceCream,
  FaMartiniGlass,
  FaMugHot,
  FaPhone,
  FaPizzaSlice,
  FaShip,
  FaSquareParking,
  FaStar,
  FaTooth,
  FaUtensils,
} from 'react-icons/fa6';
import { GiDonerKebab, GiMeat } from 'react-icons/gi';

import LogoImage from '../assets/png/logopizza.png';

const MOBILE_MENU_ICONS = {
  pizza: FaPizzaSlice,
  coffee: FaMugHot,
  burger: FaBurger,
  utensils: FaUtensils,
  kebab: GiDonerKebab,
  ice_cream: FaIceCream,
  drink: FaMartiniGlass,
  fire: FaFire,
  star: FaStar,
  tooth: FaTooth,
  parking: FaSquareParking,
  ship: FaShip,
  meat: GiMeat,
  none: null,
};

const isValidHexColor = (value) => typeof value === 'string' && /^#[0-9A-Fa-f]{6}$/.test(value);

const getSafeHexColor = (value, fallback) => (isValidHexColor(value) ? value : fallback);

const hexToRgba = (hexColor, alpha) => {
  const safeColor = getSafeHexColor(hexColor, '#000000');

  const red = parseInt(safeColor.slice(1, 3), 16);
  const green = parseInt(safeColor.slice(3, 5), 16);
  const blue = parseInt(safeColor.slice(5, 7), 16);

  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

const shadeHexColor = (hexColor, amount) => {
  const safeColor = getSafeHexColor(hexColor, '#070707');

  const clamp = (value) => Math.max(0, Math.min(255, value));

  const red = clamp(parseInt(safeColor.slice(1, 3), 16) + amount);
  const green = clamp(parseInt(safeColor.slice(3, 5), 16) + amount);
  const blue = clamp(parseInt(safeColor.slice(5, 7), 16) + amount);

  return `#${[red, green, blue].map((value) => value.toString(16).padStart(2, '0')).join('')}`;
};

const logoSpin = keyframes`
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
`;

const HeaderContainer = styled.header`
  position: relative;
  z-index: 20;

  width: 100%;
`;

const HeaderContent = styled.div`
  width: min(100% - 80px, 1500px);
  min-height: 100px;

  margin: 0 auto;

  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 40px;

  @media (max-width: 900px) {
    width: min(100% - 40px, 1500px);

    gap: 20px;
  }

  @media (max-width: 600px) {
    width: min(100% - 30px, 1500px);
    min-height: 80px;

    gap: 12px;
  }
`;

const Logo = styled.a`
  display: flex;
  align-items: center;

  gap: 12px;

  flex-shrink: 0;

  color: ${({ $textColor }) => $textColor};

  text-decoration: none;
`;

const LogoImageWrapper = styled.div`
  width: 80px;
  height: 80px;

  min-width: 80px;
  min-height: 80px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  overflow: hidden;

  img {
    max-width: 100%;
    max-height: 100%;

    width: auto;
    height: auto;

    display: block;

    object-fit: contain;

    animation: ${({ $shouldRotate }) =>
      $shouldRotate
        ? css`
            ${logoSpin} 12s linear infinite
          `
        : 'none'};
  }

  @media (max-width: 600px) {
    width: 46px;
    height: 46px;

    min-width: 46px;
    min-height: 46px;
  }

  @media (prefers-reduced-motion: reduce) {
    img {
      animation: none;
    }
  }
`;

const LogoName = styled.span`
  color: ${({ $textColor }) => $textColor};

  font-size: 28px;
  font-weight: 900;

  line-height: 1;

  letter-spacing: -1px;

  white-space: nowrap;

  @media (max-width: 600px) {
    font-size: 20px;
  }

  @media (max-width: 370px) {
    font-size: 17px;
  }
`;

const Navigation = styled.nav`
  display: flex;
  align-items: center;

  gap: 50px;

  a {
    position: relative;

    color: ${({ $textColor }) => $textColor};

    font-size: 15px;
    font-weight: 700;

    text-decoration: none;

    transition: color 0.25s ease;

    &:hover {
      color: ${({ $activeColor }) => $activeColor};
    }
  }

  a:first-child {
    color: ${({ $activeColor }) => $activeColor};

    &::after {
      content: '';

      position: absolute;

      left: 0;
      bottom: -18px;

      width: 100%;
      height: 2px;

      background: ${({ $activeColor }) => $activeColor};
    }
  }

  @media (max-width: 900px) {
    display: none;
  }
`;

const HeaderActions = styled.div`
  position: relative;

  display: flex;
  align-items: center;
  justify-content: flex-end;

  gap: 12px;

  flex-shrink: 0;

  @media (max-width: 370px) {
    gap: 8px;
  }
`;

const CallButton = styled.a`
  min-height: 58px;

  padding: 0 28px;

  border-radius: 10px;

  display: flex;
  align-items: center;
  justify-content: center;

  gap: 14px;

  background: ${({ $backgroundColor }) => $backgroundColor};

  color: ${({ $textColor }) => $textColor};

  font-size: 17px;
  font-weight: 800;

  text-decoration: none;

  box-shadow: 0 12px 30px rgba(223, 32, 37, 0.25);

  transition:
    transform 0.25s ease,
    background 0.25s ease;

  &:hover {
    transform: translateY(-3px);

    background: ${({ $hoverColor }) => $hoverColor};
  }

  svg {
    font-size: 24px;
  }

  @media (max-width: 900px) {
    width: 52px;
    height: 52px;
    min-height: 52px;

    padding: 0;

    border-radius: 50%;

    span {
      display: none;
    }

    svg {
      font-size: 21px;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover {
      transform: none;
    }
  }
`;

const MobileMenuButton = styled.button`
  position: relative;

  width: 52px;
  height: 52px;

  padding: 0;

  border: 1px solid
    ${({ $isOpen, $activeColor }) => ($isOpen ? $activeColor : 'rgba(255, 255, 255, 0.28)')};

  border-radius: 50%;

  display: none;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  gap: 5px;

  background: ${({ $isOpen, $activeColor }) => ($isOpen ? $activeColor : 'rgba(8, 8, 8, 0.88)')};

  color: #ffffff;

  cursor: pointer;

  box-shadow: ${({ $isOpen, $activeColor }) =>
    $isOpen
      ? `0 0 0 5px rgba(0, 0, 0, 0.18), 0 10px 30px ${$activeColor}55`
      : '0 10px 26px rgba(0, 0, 0, 0.28)'};

  isolation: isolate;

  transform: translateZ(0);
  -webkit-transform: translateZ(0);

  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;

  transition:
    background 0.25s ease,
    border-color 0.25s ease,
    box-shadow 0.25s ease,
    transform 0.25s ease;

  span {
    position: relative;
    z-index: 1;

    width: 21px;
    height: 2px;

    display: block;

    border-radius: 999px;

    background: currentColor;

    opacity: 1;

    transform-origin: center;

    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;

    transition:
      transform 0.25s ease,
      opacity 0.2s ease,
      width 0.25s ease;
  }

  span:nth-child(1) {
    transform: ${({ $isOpen }) => ($isOpen ? 'translateY(7px) rotate(45deg)' : 'none')};
  }

  span:nth-child(2) {
    width: ${({ $isOpen }) => ($isOpen ? '8px' : '15px')};

    opacity: ${({ $isOpen }) => ($isOpen ? 0 : 1)};
  }

  span:nth-child(3) {
    transform: ${({ $isOpen }) => ($isOpen ? 'translateY(-7px) rotate(-45deg)' : 'none')};
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      transform: translateY(-2px) translateZ(0);
    }
  }

  &:focus-visible {
    outline: 3px solid #ffffff;
    outline-offset: 3px;
  }

  @media (max-width: 900px) {
    display: flex;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    span {
      transition: none;
    }

    &:hover {
      transform: translateZ(0);
    }
  }
`;

const ScrollToTopButton = styled.button`
  position: fixed;
  right: max(20px, env(safe-area-inset-right));
  bottom: max(20px, env(safe-area-inset-bottom));
  z-index: 60;

  width: 50px;
  height: 50px;

  padding: 0;

  border: 1px solid rgba(255, 255, 255, 0.32);
  border-radius: 50%;

  display: flex;
  align-items: center;
  justify-content: center;

  background: ${({ $backgroundColor }) => $backgroundColor};

  color: ${({ $textColor }) => $textColor};

  cursor: pointer;

  box-shadow:
    0 14px 34px rgba(0, 0, 0, 0.32),
    0 0 0 4px rgba(0, 0, 0, 0.12);

  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  visibility: ${({ $isVisible }) => ($isVisible ? 'visible' : 'hidden')};

  pointer-events: ${({ $isVisible }) => ($isVisible ? 'auto' : 'none')};

  transform: ${({ $isVisible }) =>
    $isVisible ? 'translateY(0) scale(1)' : 'translateY(14px) scale(0.9)'};

  transition:
    opacity 0.22s ease,
    visibility 0s linear ${({ $isVisible }) => ($isVisible ? '0s' : '0.22s')},
    transform 0.22s ease,
    background 0.22s ease;

  svg {
    font-size: 20px;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background: ${({ $hoverColor }) => $hoverColor};

      transform: translateY(-3px) scale(1);
    }
  }

  &:focus-visible {
    outline: 3px solid #ffffff;
    outline-offset: 3px;
  }

  @media (max-width: 600px) {
    right: max(16px, env(safe-area-inset-right));
    bottom: max(16px, env(safe-area-inset-bottom));

    width: 46px;
    height: 46px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover {
      transform: none;
    }
  }
`;

const MobileMenuPanel = styled.div`
  position: absolute;

  top: calc(100% + 16px);
  right: 0;

  width: min(310px, calc(100vw - 30px));

  padding: 22px 18px 16px;

  border: 1px solid ${({ $activeColor }) => $activeColor};
  border-radius: 26px 26px 26px 9px;

  background:
    radial-gradient(
      circle at 80% 0%,
      ${({ $activeColor }) => hexToRgba($activeColor, 0.27)} 0%,
      transparent 38%
    ),
    linear-gradient(
      150deg,
      ${({ $backgroundColor }) => hexToRgba(shadeHexColor($backgroundColor, 18), 0.98)} 0%,
      ${({ $backgroundColor }) => hexToRgba($backgroundColor, 0.99)} 65%,
      ${({ $backgroundColor }) => hexToRgba(shadeHexColor($backgroundColor, -10), 0.99)} 100%
    );

  box-shadow:
    0 24px 60px rgba(0, 0, 0, 0.55),
    inset 0 0 0 1px rgba(255, 255, 255, 0.06),
    inset 0 -20px 45px rgba(0, 0, 0, 0.38);

  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};

  pointer-events: ${({ $isOpen }) => ($isOpen ? 'auto' : 'none')};

  transform: ${({ $isOpen }) =>
    $isOpen
      ? 'perspective(700px) translateY(0) rotateX(0deg) scale(1)'
      : 'perspective(700px) translateY(-12px) rotateX(-12deg) scale(0.94)'};

  transform-origin: top right;

  transition:
    opacity 0.25s ease,
    transform 0.3s ease,
    visibility 0s linear ${({ $isOpen }) => ($isOpen ? '0s' : '0.3s')};

  &::before {
    content: '';

    position: absolute;

    top: 9px;
    left: 50%;

    width: 76px;
    height: 5px;

    border-radius: 999px;

    background: linear-gradient(90deg, #4d4d4d, #c5c5c5, #4d4d4d);

    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.65);

    transform: translateX(-50%);
  }

  &::after {
    content: '';

    position: absolute;

    right: 22px;
    bottom: 8px;
    left: 22px;

    height: 2px;

    border-radius: 999px;

    background: linear-gradient(
      90deg,
      transparent,
      ${({ $activeColor }) => $activeColor},
      transparent
    );

    opacity: 0.8;
  }

  @media (min-width: 901px) {
    display: none;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const MobileMenuHeader = styled.div`
  min-height: 58px;

  padding: 8px 6px 14px;

  display: flex;
  align-items: center;

  gap: 13px;

  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const OvenIcon = styled.div`
  width: 42px;
  height: 42px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  border: 1px solid ${({ $activeColor }) => $activeColor};
  border-radius: 50%;

  background: rgba(0, 0, 0, 0.55);

  color: ${({ $activeColor }) => $activeColor};

  box-shadow:
    0 0 20px ${({ $activeColor }) => hexToRgba($activeColor, 0.33)},
    inset 0 0 12px rgba(255, 255, 255, 0.05);

  svg {
    font-size: 19px;
  }
`;

const MobileMenuTitle = styled.div`
  display: flex;
  flex-direction: column;

  gap: 3px;

  strong {
    color: ${({ $titleColor }) => $titleColor};

    font-size: 15px;
    font-weight: 900;

    letter-spacing: 1.5px;
  }

  span {
    color: ${({ $subtitleColor }) => $subtitleColor};

    font-size: 11px;
    font-weight: 600;

    letter-spacing: 0.6px;
  }
`;

const MobileNavigation = styled.nav`
  padding: 8px 0 5px;

  display: flex;
  flex-direction: column;
`;

const MobileNavigationLink = styled.a`
  min-height: 58px;

  padding: 0 8px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 16px;

  border-bottom: 1px solid rgba(255, 255, 255, 0.08);

  color: ${({ $textColor }) => $textColor};

  text-decoration: none;

  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};

  transform: ${({ $isOpen }) => ($isOpen ? 'translateX(0)' : 'translateX(18px)')};

  transition:
    color 0.2s ease,
    padding-left 0.2s ease,
    opacity 0.25s ease ${({ $index }) => `${0.06 + $index * 0.05}s`},
    transform 0.25s ease ${({ $index }) => `${0.06 + $index * 0.05}s`};

  &:last-child {
    border-bottom: 0;
  }

  &:hover,
  &:focus-visible {
    padding-left: 14px;

    color: ${({ $activeColor }) => $activeColor};
  }

  &:focus-visible {
    outline: 2px solid ${({ $activeColor }) => $activeColor};
    outline-offset: -2px;
  }

  strong {
    font-size: 15px;
    font-weight: 850;

    letter-spacing: 0.8px;
  }
`;

// const NavigationNumber = styled.span`
//   color: ${({ $activeColor }) => $activeColor};

//   font-size: 11px;
//   font-weight: 900;

//   letter-spacing: 1px;
// `;

function Header({ topSettings }) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrollTopVisible, setIsScrollTopVisible] = useState(false);

  const headerRef = useRef(null);
  const menuButtonRef = useRef(null);

  const siteName = topSettings?.site_name ?? 'MiaPizzeria';

  const logoUrl = topSettings?.logo || LogoImage;
  const logoRotation = topSettings?.logo_rotation ?? true;

  const mobileMenuIcon = topSettings?.mobile_menu_icon ?? 'pizza';
  const mobileMenuTitle = topSettings?.mobile_menu_title?.trim() || 'MENU';
  const mobileMenuSubtitle = topSettings?.mobile_menu_subtitle?.trim() ?? 'Seleziona una sezione';

  const mobileMenuAccentColor = getSafeHexColor(topSettings?.mobile_menu_accent_color, '#e4002b');

  const mobileMenuBackgroundColor = getSafeHexColor(
    topSettings?.mobile_menu_background_color,
    '#070707',
  );

  const mobileMenuTitleColor = getSafeHexColor(topSettings?.mobile_menu_title_color, '#ffffff');

  const mobileMenuSubtitleColor = getSafeHexColor(
    topSettings?.mobile_menu_subtitle_color,
    '#8c8c8c',
  );

  const mobileMenuLinksColor = getSafeHexColor(topSettings?.mobile_menu_links_color, '#ffffff');

  const MobileMenuIconComponent = Object.prototype.hasOwnProperty.call(
    MOBILE_MENU_ICONS,
    mobileMenuIcon,
  )
    ? MOBILE_MENU_ICONS[mobileMenuIcon]
    : FaPizzaSlice;

  const navigationHome = topSettings?.navigation_home ?? 'HOME';

  const navigationMenu = topSettings?.navigation_menu ?? 'MENU';

  const navigationAbout = topSettings?.navigation_about ?? 'CHI SIAMO';

  const navigationContacts = topSettings?.navigation_contacts ?? 'CONTATTI';

  const navigationTextColor = topSettings?.navigation_text_color ?? '#ffffff';

  const navigationActiveColor = topSettings?.navigation_active_color ?? '#df2025';

  const phoneNumber = topSettings?.phone_number ?? '+39 123 456 7890';

  const phoneLink = topSettings?.phone_link ?? 'tel:+391234567890';

  const callButtonText = topSettings?.call_button_text ?? 'CHIAMA ORA';

  const showCallButton = topSettings?.show_call_button ?? true;

  const callButtonBackgroundColor = topSettings?.call_button_background_color ?? '#df2025';

  const callButtonTextColor = topSettings?.call_button_text_color ?? '#ffffff';

  const callButtonHoverColor = topSettings?.call_button_hover_color ?? '#f1262c';

  const mobileNavigationItems = [
    {
      key: 'home',
      href: '#home',
      label: navigationHome,
    },
    {
      key: 'menu',
      href: '#menu',
      label: navigationMenu,
    },
    {
      key: 'about',
      href: '#contatti',
      label: navigationAbout,
    },
    {
      key: 'contacts',
      href: '#contatti',
      label: navigationContacts,
    },
  ];

  useEffect(() => {
    if (!isMobileMenuOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsMobileMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };

    const handlePointerDown = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('pointerdown', handlePointerDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);

      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const desktopMediaQuery = window.matchMedia('(min-width: 901px)');

    const handleDesktopChange = (event) => {
      if (event.matches) {
        setIsMobileMenuOpen(false);
      }
    };

    desktopMediaQuery.addEventListener('change', handleDesktopChange);

    return () => {
      desktopMediaQuery.removeEventListener('change', handleDesktopChange);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const shouldShowButton = window.scrollY > Math.max(420, window.innerHeight * 0.65);

      setIsScrollTopVisible((currentValue) =>
        currentValue === shouldShowButton ? currentValue : shouldShowButton,
      );
    };

    handleScroll();

    window.addEventListener('scroll', handleScroll, {
      passive: true,
    });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleMobileNavigation = (event, href) => {
    event.preventDefault();

    const targetElement = document.querySelector(href);

    event.currentTarget.blur();

    setIsMobileMenuOpen(false);

    window.requestAnimationFrame(() => {
      menuButtonRef.current?.getBoundingClientRect();

      window.requestAnimationFrame(() => {
        if (!targetElement) {
          return;
        }

        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        targetElement.scrollIntoView({
          behavior: prefersReducedMotion ? 'auto' : 'smooth',
          block: 'start',
        });

        window.history.replaceState(null, '', href);
      });
    });
  };

  const handleScrollToTop = () => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const homeElement = document.querySelector('#home');

    if (homeElement) {
      homeElement.scrollIntoView({
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
        block: 'start',
      });
    } else {
      window.scrollTo({
        top: 0,
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
      });
    }

    window.history.replaceState(null, '', '#home');
  };

  return (
    <HeaderContainer ref={headerRef}>
      <HeaderContent>
        <Logo href="#home" $textColor={navigationTextColor}>
          <LogoImageWrapper $shouldRotate={logoRotation}>
            <img src={logoUrl} alt={siteName} />
          </LogoImageWrapper>

          <LogoName $textColor={navigationTextColor}>{siteName}</LogoName>
        </Logo>

        <Navigation
          aria-label="Navigazione principale"
          $textColor={navigationTextColor}
          $activeColor={navigationActiveColor}
        >
          <a href="#home">{navigationHome}</a>
          <a href="#menu">{navigationMenu}</a>
          <a href="#contatti">{navigationAbout}</a>
          <a href="#contatti">{navigationContacts}</a>
        </Navigation>

        <HeaderActions>
          {showCallButton && (
            <CallButton
              href={phoneLink}
              aria-label={`${callButtonText}: ${phoneNumber}`}
              $backgroundColor={callButtonBackgroundColor}
              $textColor={callButtonTextColor}
              $hoverColor={callButtonHoverColor}
            >
              <FaPhone aria-hidden="true" />

              <span>{callButtonText}</span>
            </CallButton>
          )}

          <MobileMenuButton
            ref={menuButtonRef}
            type="button"
            aria-label={isMobileMenuOpen ? 'Chiudi il menu' : 'Apri il menu'}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-navigation-panel"
            $isOpen={isMobileMenuOpen}
            $activeColor={mobileMenuAccentColor}
            onClick={() => setIsMobileMenuOpen((currentValue) => !currentValue)}
          >
            <span />
            <span />
            <span />
          </MobileMenuButton>

          <MobileMenuPanel
            id="mobile-navigation-panel"
            aria-hidden={!isMobileMenuOpen}
            $isOpen={isMobileMenuOpen}
            $activeColor={mobileMenuAccentColor}
            $backgroundColor={mobileMenuBackgroundColor}
          >
            <MobileMenuHeader>
              {MobileMenuIconComponent && (
                <OvenIcon $activeColor={mobileMenuAccentColor}>
                  <MobileMenuIconComponent aria-hidden="true" />
                </OvenIcon>
              )}

              <MobileMenuTitle
                $titleColor={mobileMenuTitleColor}
                $subtitleColor={mobileMenuSubtitleColor}
              >
                <strong>{mobileMenuTitle}</strong>

                {mobileMenuSubtitle && <span>{mobileMenuSubtitle}</span>}
              </MobileMenuTitle>
            </MobileMenuHeader>

            <MobileNavigation aria-label="Navigazione mobile">
              {mobileNavigationItems.map((item, index) => (
                <MobileNavigationLink
                  key={item.key}
                  href={item.href}
                  tabIndex={isMobileMenuOpen ? 0 : -1}
                  $isOpen={isMobileMenuOpen}
                  $index={index}
                  $activeColor={mobileMenuAccentColor}
                  $textColor={mobileMenuLinksColor}
                  onClick={(event) => handleMobileNavigation(event, item.href)}
                >
                  <strong>{item.label}</strong>

                  {/* <NavigationNumber $activeColor={navigationActiveColor}>
                    {String(index + 1).padStart(2, '0')}
                  </NavigationNumber> */}
                </MobileNavigationLink>
              ))}
            </MobileNavigation>
          </MobileMenuPanel>
        </HeaderActions>
      </HeaderContent>

      <ScrollToTopButton
        type="button"
        aria-label="Torna all'inizio"
        aria-hidden={!isScrollTopVisible}
        tabIndex={isScrollTopVisible ? 0 : -1}
        $isVisible={isScrollTopVisible}
        $backgroundColor={mobileMenuAccentColor}
        $hoverColor={shadeHexColor(mobileMenuAccentColor, -22)}
        $textColor={mobileMenuTitleColor}
        onClick={handleScrollToTop}
      >
        <FaArrowUp aria-hidden="true" />
      </ScrollToTopButton>
    </HeaderContainer>
  );
}

export default Header;
